/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type FormattedContentStackPost } from '@/features/contentstack/interface/models';
import { getContentStackBlogContent } from '../../../features/contentstack/lib/blog/getContentStackBlogContent';
import { getAxiosForClient } from '@/lib/axios';
import { type PageContextType } from '@/next-types';
import { type QueryClient, type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useLocale } from '@/hooks/useLocale';
import { getBlogPath } from '../utils/getBlogPath';
import { type NextApiRequest } from 'next';
import { type ParsedUrlQuery } from 'querystring';

const URL = `/api/contentstack/blog`;

const getContentStackBlogData = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getCSBlogKey>>) => {
  const [, , { blogPath }] = queryKey;

  const { data } = await getAxiosForClient().get<FormattedContentStackPost>(URL, {
    params: {
      blogPath,
    },
  });
  return data;
};

const getCSBlogKey = ({ blogPath }: { blogPath: string }) => ['cms', 'blog', { blogPath }] as const;

export const useContentStackBlogData = () => {
  const router = useLegacyRouter();
  const locale = useLocale();

  const { fullPath: blogPath } = getBlogPath(locale, router.query, router.pathname);

  return useQuery({
    queryKey: getCSBlogKey({ blogPath }),
    queryFn: getContentStackBlogData,
    enabled: !!blogPath,
  });
};

export const prefetchContentStackBlogData = ({
  queryClient,
  blogPath,
  req,
  query,
}: {
  queryClient: QueryClient;
  req: NextApiRequest | PageContextType['req'];
  blogPath: string;
  query: ParsedUrlQuery;
}) => {
  return queryClient.prefetchQuery({
    queryKey: getCSBlogKey({ blogPath }),
    queryFn: () =>
      getContentStackBlogContent(req, {
        ...query,
        blogPath,
      }),
  });
};

export const getContentStackBlogDataFromCache = ({
  queryClient,
  blogPath,
}: {
  queryClient: QueryClient;
  blogPath: string;
}) => {
  return queryClient.getQueryData<FormattedContentStackPost>(getCSBlogKey({ blogPath }));
};
